import { useState, useEffect, useRef } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { Box, List, CssBaseline, Divider, Drawer, AppBar } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import { DialogBox, Ribbon } from "components";
import { setVerifyFieldsEdit } from "dataStore/verifyFieldsEdit";
import { verifyLoginAction } from "dataStore/auth";
import { setOrderDetailsAction } from "dataStore/orderDetails";
import { remUserDataAction } from "dataStore/userProfile";
import { setTab } from "dataStore/selectedTab";
import LiveEventsPanel from "components/LiveEventsPanel";

import {
  SIDE_MENU,
  DrawerHeader,
  closedMixin,
  openedMixin,
  CustomListItemText,
  StyledListItem,
  drawerWidth,
  ToggleSidebar,
  AppHeader,
  CustomListItemIcon,
} from "./SidebarCustomComponents";
import Support from "components/Support";

const CustomAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  boxShadow: "unset",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const CustomDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  position: "absolute",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));
let clonePath = "";

export default function SideBar({ children }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [selectedPath, setPath] = useState("");
  const [expandedMenus, setExpandedMenus] = useState([]);
  const [isSideMenuScrolling, setIsSideMenuScrolling] = useState(false);
  const sideMenuScrollTimer = useRef(null);

  useEffect(() => {
    if (!open) {
      setExpandedMenus([]);
    }
  }, [open]);

  useEffect(
    () => () => {
      if (sideMenuScrollTimer.current) {
        clearTimeout(sideMenuScrollTimer.current);
      }
    },
    [],
  );

  const dispatch = useDispatch();
  const navigation = useNavigate();
  const location = useLocation();

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const verifyFieldsEdit = useSelector((state) => state.verifyFieldsEdit);
  const userData = useSelector((state) => state.userData);
  const dashboardPathParams = useSelector((state) => state.dashboardPathParams);
  const orderListPathParams = useSelector((state) => state.orderListPathParams);

  const redirectToHome = () => {
    dispatch(verifyLoginAction({ value: false, token: "" }));
    dispatch(setOrderDetailsAction({}));
    dispatch(remUserDataAction());
    dispatch(setTab(null));

    navigation("/");
  };

  const setDefaultFilter = (path) => {
    switch (path) {
      case "dashboard":
        return `/${path}?${dashboardPathParams}`;
      case "order-list":
        if (location.pathname === "/dashboard") {
          return `/${path}?filterValue=today&${dashboardPathParams}`;
        } else {
          return orderListPathParams
            ? `/${path}?${orderListPathParams}`
            : `/${path}?filterValue=today`;
        }
      default:
        return `/${path}`;
    }
  };

  const navigateToPage = (path) => {
    const { isFieldsEdit } = verifyFieldsEdit;
    clonePath = path;
    if (isFieldsEdit) {
      setIsOpenConfirmModal(true);
    } else {
      const newPath = setDefaultFilter(path);
      navigation(newPath);
      setPath(path);
    }
  };

  useEffect(() => {
    if (location) {
      setPath(location.pathname.split("/")[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleExitPage = () => {
    dispatch(setVerifyFieldsEdit(false));
    setIsOpenConfirmModal(false);
    navigation(`/${clonePath}`);
  };

  const handleSideMenuScroll = () => {
    setIsSideMenuScrolling(true);

    if (sideMenuScrollTimer.current) {
      clearTimeout(sideMenuScrollTimer.current);
    }

    sideMenuScrollTimer.current = setTimeout(() => {
      setIsSideMenuScrolling(false);
    }, 700);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <CustomAppBar position="fixed" open={open}>
        <Ribbon />
        <AppHeader
          redirectToHome={redirectToHome}
          userName={userData?.name}
        />
      </CustomAppBar>
      <LiveEventsPanel />
      <CustomDrawer
        variant="permanent"
        open={open}
        sx={{
          "& .MuiDrawer-paper": {
            marginTop: 10,
            height: "100%",
            boxShadow: open && theme.shadows[0],
            boxSizing: "border-box",
            paddingBottom: 25,
            backgroundColor: theme.palette.bgColor,
            borderRight: `1px solid ${theme.palette.grey["100"]}`,
            overflow: "hidden",
          },
        }}
      >
        <Divider />
        <List
          onScroll={handleSideMenuScroll}
          style={{
            display: "flex",
            flexDirection: "column",
          }}
          sx={{
            flex: 1,
            overflowX: "hidden",
            overflowY: "auto",
            scrollbarWidth: isSideMenuScrolling ? "thin" : "none",
          }}
        >
          <ToggleSidebar
            handleDrawerClose={handleDrawerClose}
            handleDrawerOpen={handleDrawerOpen}
            open={open}
          />
          {SIDE_MENU.map((menu) => {
            const showParent = userData?.allowed_URL?.[menu?.label];
            const childPaths = (menu.children || []).map((c) => c.path);
            const isChildActive = childPaths.includes(selectedPath);
            const isExpanded = expandedMenus.includes(menu.path);
            const shouldShowChildren = open && (isExpanded || isChildActive);

            const handleParentClick = () => {
              if (menu.children && menu.children.length) {
                if (!open) {
                  setOpen(true);
                  setExpandedMenus((prev) =>
                    prev.includes(menu.path) ? prev : [...prev, menu.path],
                  );
                  navigateToPage(menu.path);
                  return;
                }

                setExpandedMenus((prev) =>
                  prev.includes(menu.path)
                    ? prev.filter((path) => path !== menu.path)
                    : [...prev, menu.path],
                );
                navigateToPage(menu.path);
              } else {
                navigateToPage(menu.path);
              }
            };

            return (
              <div key={menu.label}>
                <StyledListItem
                  style={{
                    fontFamily: "unset",
                    display: showParent ? "" : "none",
                  }}
                  onClick={handleParentClick}
                >
                  <CustomListItemIcon selectedPath={selectedPath} menu={menu} />
                  <CustomListItemText primary={menu.label} />
                </StyledListItem>

                {menu.children && shouldShowChildren
                  ? menu.children.map((child) => (
                      <StyledListItem
                        key={child.label}
                        style={{
                          backgroundColor: theme.palette.grey[200],
                          paddingLeft: 64,
                          paddingTop: 10,
                          paddingBottom: 10,
                          fontFamily: "unset",
                          display: userData?.allowed_URL?.[child?.label]
                            ? ""
                            : "none",
                        }}
                        onClick={() => navigateToPage(child.path)}
                      >
                        <CustomListItemText primary={child.label} />
                      </StyledListItem>
                    ))
                  : null}
              </div>
            );
          })}
          <Support open={open} />
        </List>
      </CustomDrawer>
      <Box
        component="main"
        sx={{ flexGrow: 1, minWidth: 0, overflowX: "hidden", p: 3, pl: 14 }}
        onMouseOver={handleDrawerClose}
      >
        <DrawerHeader />
        {children}
      </Box>
      <DialogBox
        handleClose={() => setIsOpenConfirmModal(false)}
        open={isOpenConfirmModal}
        handleConfirm={handleExitPage}
      />
    </Box>
  );
}

SideBar.propTypes = {
  children: PropTypes.node,
};
